import * as React from 'react';

import {
    useDataTable, useTranslation, useRemoteData,
    useMessage, useLoading, useSearch, Loading, Tag
} from '@components';
import {
    IWorker, IWork, ISecurity, ModuleManager
} from '@models';
import * as S from '@utils/security';
import './WorkerJobsDialog.scss'

type GetWorkerF = (workId: number, id: number) => Promise<IWorker>;
type GetJobF = (workId: number, jobId: number) => Promise<any>;

export interface IProps {
    activateJobHasWorker: Function;
    requestClose: Function;
    saveJobHasWorker: Function;
    security: ISecurity;
    worker: IWorker;
    workerId: number;
    getWorker: GetWorkerF;
    work: IWork;
    removeJobHasWorker: Function;
    moduleManager: ModuleManager;
    getJob: GetJobF;
}

type SearchR = {
    endDate?: Date;
    jobId: number;
    jobHasWorkerId?: number;
    jobHasContractorId?: number;
    isRelated?: boolean;
    currentStatusType?: number;
    parentContractorName?: string;
    name?: string;
    code?: string;
    description?: string;
}

function WorkerJobsFormImpl(props: IProps) {
    const { t } = useTranslation();
    const loading = useLoading();
    const messages = useMessage();

    const search = useSearch<SearchR>({
        search: 'contractorJobsHasWorker.list',
        normalizeKeys: true,
        workId: props.work.id,
        filters: {
            workerId: props.workerId,
            contractorId: props.worker.contractorId
        }
    });

    React.useEffect(() => {
        search.doSearch();
    }, []);

    const [jobPerms, setJobPerms] = React.useState<Record<number, { add: boolean; remove: boolean }>>({});

    React.useEffect(() => {
        if (!search.value || !search.value.length) {
            setJobPerms({});
            return;
        }

        const jobIds = Array.from(new Set(search.value.map(r => r.jobId)));
        let cancelled = false;

        const load = async () => {
            const results: Record<number, { add: boolean; remove: boolean }> = {};

            const isGestor = props.security.isGestor();

            const promises = jobIds.map(async (jobId) => {
                try {
                    const resp = await props.getJob(props.work.id, jobId);
                    if (cancelled) return;
                    const job = resp.data?.work?.job;
                    if (!job) return;

                    const responsibleIds = job.departments
                        ?.filter((d: any) => d.isActive !== false)
                        ?.map((d: any) => d.responsibleId) ?? [];
                    const isResponsible = responsibleIds.some((rid: number) => props.security.getWorkerIds().includes(rid));

                    const evalUser = {
                        ...(props.security.user ?? {}),
                        workerIds: props.security.getWorkerIds(),
                        contractorIds: (props.security as any).getContractorIds?.() ?? [],
                    };
                    const add = isGestor || isResponsible || S.hasPermission('jobHasWorker.add', { user: evalUser as any, work: props.work, job });
                    const remove = isGestor || isResponsible || S.hasPermission('jobHasWorker.remove', { user: evalUser as any, work: props.work, job });

                    if (!cancelled) {
                        results[jobId] = { add, remove };
                    }
                } catch (e) {
                    console.error('Failed to fetch permissions for job', jobId, e);
                }
            });

            await Promise.all(promises);

            if (!cancelled) {
                setJobPerms(results);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [search.value, props.security, props.work.id]);

    const jobName = (d: SearchR) => {
        return <><Tag value={d.code} /><span className='sm pd-left' title={d.name}>{d.name}</span></>
    }

    const sortedValue = [...(search.value ?? [])].sort((a, b) => b.jobId - a.jobId);

    const dataTable = useDataTable<SearchR>({
        columns: [
            { title: 'Job', render: (d: any) => jobName(d) },
            {
                title: 'Description',
                field: 'description',
                render: (d: SearchR) => <span title={d.description}>
                    {d.description}
                </span>
            },
            {
                title: 'Contractor',
                field: 'parentContractorName',
                render: (d: SearchR) => <span title={d.parentContractorName}>
                    {d.parentContractorName}
                </span>
            },
            {
                title: 'Associated',
                delegate: 'boolean',
                field: 'isRelated',
                className: 'td-md'
            },
            {
                title: 'End date',
                delegate: 'date',
                field: 'endDate',
                className: 'td-lg'
            },
        ],
        actions: [
            {
                actions: [
                    {
                        title: t('Associate'),
                        onClick: d => saveWorkerRelation(d.jobId, d.jobHasContractorId),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p === undefined) return true;
                            if (!p.add) return true;
                            return d.isRelated === true || d.currentStatusType === 3;
                        },
                    },
                    {
                        title: t('Reactivate'),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p !== undefined && !p.add) return true;
                            return d.isRelated !== true || d.endDate == null;
                        },
                        onClick: d => reactiveWorkerRelation(d.jobId, d.jobHasWorkerId, d.jobHasContractorId, d)
                    },
                    {
                        title: t('Finalize'),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p !== undefined && !p.remove) return true;
                            return !d.isRelated || d.endDate != null;
                        },
                        onClick: d => removeWorkerRelation(d.jobId, d.jobHasWorkerId)
                    },
                ]
            }
        ],
        data: sortedValue,
        loading: loading,
    });

    const saveWorkerRelation = loading.wrap(async (jobId: number, jobHasContractorId: number) => {
        const data = {
            workerId: props.workerId,
            jobId,
            jobHasContractorId: jobHasContractorId,
        };
        const resp = await props.saveJobHasWorker(props.work.id, data);
        messages.set(resp);
        await search.doSearch();
    });

    const removeWorkerRelation = loading.wrap(async (jobId: number, jobHasWorkerId: number) => {
        const resp = await props.removeJobHasWorker(props.work.id, jobId, jobHasWorkerId);
        messages.set(resp);
        await search.doSearch();
    });

    const reactiveWorkerRelation = loading.wrap(async (jobId: number, jobHasWorkerId: number, jobHasContractorId: number, d: SearchR) => {
        const resp = await props.activateJobHasWorker(props.work.id, jobId, props.workerId, d.jobHasWorkerId);
        messages.set(resp);
        await search.doSearch();
    });

    return <div className='WorkerJobsDialog'>
        {search.renderLoading()}
        {dataTable()}
        <div className='footer r r-end'>
            <button onClick={() => props.requestClose()}>{t('Close')}</button>
        </div>
    </div>
}

export function WorkerJobsDialog(props: IProps) {
    const worker = useRemoteData<IWorker>(props.getWorker, {
        parameters: [props.work.id, props.workerId]
    });

    return <div>
        {worker.renderLoading()}
        {worker.value && <WorkerJobsFormImpl
            {...props}
            worker={worker.value} />}
    </div>
}


// export function WorkerJobsForm(props: IProps) {
//TODO: llevar al modulo de Azsa
//     const res = props
//         .moduleManager
//         .renderComponent<IProps>('WorkerJobsForm', { ...props }, WorkerJobsFormImpl);

//     return res;
// }
